import { action, computed, makeObservable, observable } from "mobx"
import { QueueModeSounds, type QueueModeSound } from "./queueModeSounds"

export const DEFAULT_QUEUE_MAX_CONCURRENT = 10
export const QUEUE_MODE_IDLE_TIMEOUT_MS = 5 * 60_000
const QUEUE_MODE_HEARTBEAT_MS = 15_000
const QUEUE_MODE_ACTIVITY_THROTTLE_MS = 1_000
export const DEFAULT_QUEUE_SKIP_MINUTES = 60
export const MIN_QUEUE_MAX_CONCURRENT = 1
export const MAX_QUEUE_MAX_CONCURRENT = 99
const MAX_DAILY_ELAPSED_MS = 26 * 60 * 60_000
const QUEUE_MODE_SETTINGS_KEY_PREFIX = "wildlark:queue-mode:v1"

export interface QueueModeTask {
    taskId: string
    workspaceId: string
    isMajor?: boolean
}

export type QueueModeHandledKind = "close" | "snooze" | "work"

interface PersistedQueueModeDay {
    activeElapsedMs: number
    closedTaskCount: number
    dayKey: string
    handledTaskCount: number
    handledTaskIds: string[]
    isActive: boolean
    launchedTaskCount: number
    snoozedTaskCount: number
    trackedTaskId: string | null
}

interface PersistedQueueModeState {
    daily: PersistedQueueModeDay | null
    maxConcurrent: number
    soundsEnabled: boolean
}

function sameTaskQueue(left: readonly QueueModeTask[], right: readonly QueueModeTask[]): boolean {
    return (
        left.length === right.length &&
        left.every(
            (task, index) =>
                task.taskId === right[index]?.taskId && task.workspaceId === right[index]?.workspaceId && !!task.isMajor === !!right[index]?.isMajor
        )
    )
}

function clampMaxConcurrent(value: number): number {
    if (!Number.isFinite(value)) return DEFAULT_QUEUE_MAX_CONCURRENT
    return Math.min(MAX_QUEUE_MAX_CONCURRENT, Math.max(MIN_QUEUE_MAX_CONCURRENT, Math.round(value)))
}

function nonnegativeInteger(value: unknown, maximum = Number.MAX_SAFE_INTEGER): number {
    return typeof value === "number" && Number.isFinite(value) ? Math.min(maximum, Math.max(0, Math.round(value))) : 0
}

function localDayKey(timestampMs: number): string {
    const date = new Date(timestampMs)
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const day = date.getDate().toString().padStart(2, "0")
    return `${year}-${month}-${day}`
}

function localDayStartMs(timestampMs: number): number {
    const date = new Date(timestampMs)
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

function nextLocalDayStartMs(timestampMs: number): number {
    const date = new Date(timestampMs)
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1).getTime()
}

function queueModeSettingsKey(scope: string): string {
    return `${QUEUE_MODE_SETTINGS_KEY_PREFIX}:${scope}`
}

function parsePersistedDay(value: unknown): PersistedQueueModeDay | null {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return null

    const dayKey = Reflect.get(value, "dayKey")
    if (typeof dayKey !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) return null

    const handledTaskIdsValue = Reflect.get(value, "handledTaskIds")
    const handledTaskIds = Array.isArray(handledTaskIdsValue)
        ? handledTaskIdsValue.filter((taskId): taskId is string => typeof taskId === "string").slice(0, 10_000)
        : []
    const trackedTaskIdValue = Reflect.get(value, "trackedTaskId")

    return {
        activeElapsedMs: nonnegativeInteger(Reflect.get(value, "activeElapsedMs"), MAX_DAILY_ELAPSED_MS),
        closedTaskCount: nonnegativeInteger(Reflect.get(value, "closedTaskCount")),
        dayKey,
        handledTaskCount: nonnegativeInteger(Reflect.get(value, "handledTaskCount")),
        handledTaskIds,
        isActive: Reflect.get(value, "isActive") === true,
        launchedTaskCount: nonnegativeInteger(Reflect.get(value, "launchedTaskCount")),
        snoozedTaskCount: nonnegativeInteger(Reflect.get(value, "snoozedTaskCount")),
        trackedTaskId: typeof trackedTaskIdValue === "string" ? trackedTaskIdValue : null,
    }
}

function loadQueueModeState(scope: string): PersistedQueueModeState {
    if (typeof window === "undefined") return { daily: null, maxConcurrent: DEFAULT_QUEUE_MAX_CONCURRENT, soundsEnabled: true }

    try {
        const serialized = window.localStorage.getItem(queueModeSettingsKey(scope))
        if (!serialized) return { daily: null, maxConcurrent: DEFAULT_QUEUE_MAX_CONCURRENT, soundsEnabled: true }
        const value: unknown = JSON.parse(serialized)
        if (typeof value !== "object" || value === null || Array.isArray(value)) {
            return { daily: null, maxConcurrent: DEFAULT_QUEUE_MAX_CONCURRENT, soundsEnabled: true }
        }
        const maxConcurrent = Reflect.get(value, "maxConcurrent")
        return {
            daily: parsePersistedDay(Reflect.get(value, "daily")),
            maxConcurrent: typeof maxConcurrent === "number" ? clampMaxConcurrent(maxConcurrent) : DEFAULT_QUEUE_MAX_CONCURRENT,
            soundsEnabled: Reflect.get(value, "soundsEnabled") !== false,
        }
    } catch {
        return { daily: null, maxConcurrent: DEFAULT_QUEUE_MAX_CONCURRENT, soundsEnabled: true }
    }
}

export class QueueModeManager {
    @observable advanceAtMs: number | null = null
    @observable capacityWaitSourceTaskId: string | null = null
    @observable isActive = false
    // Explicit Play only; this visual cue is never persisted or restored.
    @observable launchStartedAtMs: number | null = null
    @observable isAppFocused = typeof document !== "undefined" && document.hasFocus() && document.visibilityState === "visible"
    @observable isBrowsingUnavailableTask = false
    @observable isIdle = false
    @observable isWaitingForCapacity = false
    @observable closedTaskCount = 0
    @observable handledTaskCount = 0
    @observable launchedTaskCount = 0
    @observable maxConcurrent = DEFAULT_QUEUE_MAX_CONCURRENT
    @observable runId = 0
    @observable runningTaskCount = 0
    @observable snoozedTaskCount = 0
    @observable soundsEnabled = true
    @observable private activeElapsedMs = 0
    @observable private activeSliceStartedAtMs: number | null = null
    @observable.shallow private queueTasks: QueueModeTask[] = []
    private handledTaskIds = new Set<string>()
    private dayRolloverTimer: number | null = null
    private heartbeatTimer: number | null = null
    private idleTimer: number | null = null
    private lastActivityAtMs = Date.now()
    private storageScope: string | null = null
    private trackedTaskId: string | null = null
    private workingDayKey = localDayKey(Date.now())

    constructor(private readonly sounds = new QueueModeSounds()) {
        makeObservable(this)
    }

    @computed get eligibleTaskCount(): number {
        return this.readyTasks.length
    }

    @computed get majorReadyTaskCount(): number {
        return this.readyTasks.filter((task) => task.isMajor).length
    }

    @computed get hasCapacity(): boolean {
        return this.runningTaskCount < this.maxConcurrent
    }

    @computed get remainingCapacity(): number {
        return Math.max(0, this.maxConcurrent - this.runningTaskCount)
    }

    @computed get isTimerRunning(): boolean {
        return this.activeSliceStartedAtMs !== null
    }

    private get readyTasks(): QueueModeTask[] {
        return this.queueTasks
    }

    getElapsedMs(nowMs = Date.now()): number {
        const activeThroughMs = Math.min(nowMs, this.lastActivityAtMs + QUEUE_MODE_IDLE_TIMEOUT_MS)
        if (localDayKey(nowMs) !== this.workingDayKey) {
            return this.activeSliceStartedAtMs === null ? 0 : Math.max(0, activeThroughMs - Math.max(this.activeSliceStartedAtMs, localDayStartMs(nowMs)))
        }
        if (this.activeSliceStartedAtMs === null) return this.activeElapsedMs
        return Math.min(MAX_DAILY_ELAPSED_MS, this.activeElapsedMs + Math.max(0, activeThroughMs - this.activeSliceStartedAtMs))
    }

    @action.bound setStorageScope(scope: string): void {
        if (this.storageScope === scope) return

        const nowMs = Date.now()
        this.commitActiveSlice(nowMs)
        this.persistState()
        this.clearClockTimers()
        this.sounds.dispose()
        this.launchStartedAtMs = null
        this.advanceAtMs = null
        this.capacityWaitSourceTaskId = null
        this.isBrowsingUnavailableTask = false
        this.isWaitingForCapacity = false

        this.storageScope = scope
        const persisted = loadQueueModeState(scope)
        this.maxConcurrent = persisted.maxConcurrent
        this.soundsEnabled = persisted.soundsEnabled
        const shouldResume = persisted.daily?.isActive ?? false
        const trackedTaskId = persisted.daily?.trackedTaskId ?? null
        if (persisted.daily?.dayKey === localDayKey(nowMs)) this.restoreDailyState(persisted.daily)
        else this.resetDailyState(nowMs)
        this.isActive = shouldResume
        this.trackedTaskId = trackedTaskId
        this.isIdle = false
        this.lastActivityAtMs = nowMs
        if (this.isActive) {
            this.runId += 1
            this.resumeClock(nowMs)
            this.scheduleIdleTimer()
            this.scheduleDayRollover()
        }
    }

    @action.bound setMaxConcurrent(value: number): void {
        this.maxConcurrent = clampMaxConcurrent(value)
        this.persistState()
    }

    @action.bound setSoundsEnabled(enabled: boolean): void {
        this.soundsEnabled = enabled
        this.persistState()
        if (enabled) this.playSound("work")
        else this.sounds.dispose()
    }

    prepareSounds(): void {
        if (this.isActive && this.soundsEnabled && this.isAppFocused) void this.sounds.prepare()
    }

    notifyTaskSubmitted(): void {
        this.playSound("submit")
    }

    private playSound(sound: QueueModeSound): void {
        if (this.isActive && this.soundsEnabled && this.isAppFocused) void this.sounds.play(sound)
    }

    @action.bound setRunningTaskCount(value: number): void {
        this.runningTaskCount = Math.max(0, Math.round(value))
    }

    @action.bound replaceEligibleTasks(tasks: readonly QueueModeTask[]): void {
        const majorTasks = tasks.filter((task) => task.isMajor)
        const smallTasks = tasks.filter((task) => !task.isMajor)
        const prioritizedTasks = [...majorTasks, ...smallTasks]
        if (sameTaskQueue(this.queueTasks, prioritizedTasks)) return
        this.queueTasks = prioritizedTasks
    }

    @action.bound clearEligibleTasks(): void {
        this.queueTasks = []
    }

    @action.bound start(): QueueModeTask | null {
        const firstTask = this.queueTasks[0]
        if (!firstTask) return null

        const nowMs = Date.now()
        this.ensureWorkingDay(nowMs)
        this.isActive = true
        this.launchStartedAtMs = nowMs
        this.advanceAtMs = null
        this.capacityWaitSourceTaskId = null
        this.isBrowsingUnavailableTask = false
        this.isIdle = false
        this.isWaitingForCapacity = false
        this.lastActivityAtMs = nowMs
        this.runId += 1
        this.trackedTaskId = firstTask.taskId
        this.resumeClock(nowMs)
        this.scheduleIdleTimer()
        this.scheduleDayRollover()
        this.persistState()
        this.playSound("launch")
        return firstTask
    }

    @action.bound stop(): void {
        this.sounds.dispose()
        const nowMs = Date.now()
        this.pauseClock(nowMs)
        this.advanceAtMs = null
        this.capacityWaitSourceTaskId = null
        this.isActive = false
        this.launchStartedAtMs = null
        this.isBrowsingUnavailableTask = false
        this.isIdle = false
        this.isWaitingForCapacity = false
        this.trackedTaskId = null
        this.clearDayRolloverTimer()
        this.persistState()
    }

    @action.bound setAppFocused(focused: boolean, nowMs = Date.now()): void {
        if (this.isAppFocused === focused) return
        this.isAppFocused = focused
        if (!focused) {
            this.sounds.dispose()
            this.pauseClock(nowMs)
            this.clearIdleTimer()
            return
        }

        this.isIdle = false
        this.lastActivityAtMs = nowMs
        this.ensureWorkingDay(nowMs)
        this.resumeClock(nowMs)
        this.scheduleIdleTimer()
        this.persistState()
    }

    @action.bound recordUserActivity(nowMs = Date.now()): void {
        if (!this.isIdle && nowMs - this.lastActivityAtMs < QUEUE_MODE_ACTIVITY_THROTTLE_MS) return
        if (this.activeSliceStartedAtMs !== null && nowMs >= this.lastActivityAtMs + QUEUE_MODE_IDLE_TIMEOUT_MS) {
            this.commitActiveSlice(nowMs)
        }
        this.lastActivityAtMs = nowMs
        const wasIdle = this.isIdle
        this.isIdle = false
        this.ensureWorkingDay(nowMs)
        if (this.isAppFocused) this.resumeClock(nowMs)
        this.scheduleIdleTimer()
        if (wasIdle) this.persistState()
    }

    notifyTaskSkipped(): void {
        this.playSound("skip")
    }

    isTaskEligible(taskId: string): boolean {
        return this.readyTasks.some((task) => task.taskId === taskId)
    }

    @action.bound recordHandledTask(taskId: string, kind: QueueModeHandledKind): void {
        if (!this.isActive) return
        this.ensureWorkingDay(Date.now())
        if (!this.handledTaskIds.has(taskId)) {
            this.handledTaskIds.add(taskId)
            this.handledTaskCount += 1
        }
        if (kind === "work") this.launchedTaskCount += 1
        if (kind === "snooze") this.snoozedTaskCount += 1
        if (kind === "close") this.closedTaskCount += 1
        this.persistState()
        this.playSound(kind)
    }

    @action.bound beginAdvanceCountdown(delayMs: number): number | null {
        if (!this.isActive) return null
        this.capacityWaitSourceTaskId = null
        this.isBrowsingUnavailableTask = false
        this.isWaitingForCapacity = false
        this.advanceAtMs = Date.now() + delayMs
        return this.advanceAtMs
    }

    @action.bound clearAdvanceCountdown(): void {
        this.advanceAtMs = null
    }

    @action.bound waitForCapacity(sourceTaskId: string | null = null): void {
        if (!this.isActive) return
        this.advanceAtMs = null
        this.capacityWaitSourceTaskId = sourceTaskId
        this.isBrowsingUnavailableTask = false
        this.isWaitingForCapacity = true
    }

    @action.bound clearCapacityWait(): void {
        this.capacityWaitSourceTaskId = null
        this.isWaitingForCapacity = false
    }

    @action.bound trackTask(taskId: string): void {
        if (!this.isActive) return
        this.isBrowsingUnavailableTask = false
        this.trackedTaskId = taskId
        this.persistState()
    }

    @action.bound completeHandoff(taskId: string, sourceTaskId = this.trackedTaskId): void {
        // Browsing a running/closed task retains the tracked Q task; explicit Continue supplies the actual source.
        const changedTask = sourceTaskId !== taskId
        this.trackTask(taskId)
        if (changedTask) this.playSound("handoff")
    }

    @action.bound observeTask(taskId: string, canHandle: boolean): void {
        if (!this.isActive || this.trackedTaskId === null) return
        if (taskId === this.trackedTaskId) {
            if (canHandle) {
                this.isBrowsingUnavailableTask = false
                return
            }
            if (this.advanceAtMs !== null || this.isWaitingForCapacity) return
            this.isBrowsingUnavailableTask = true
            return
        }
        this.advanceAtMs = null
        this.capacityWaitSourceTaskId = null
        this.isWaitingForCapacity = false
        if (canHandle) {
            this.isBrowsingUnavailableTask = false
            this.trackedTaskId = taskId
            this.persistState()
            return
        }
        this.isBrowsingUnavailableTask = true
    }

    nextAfterTask(taskId: string): QueueModeTask | null {
        if (!this.isActive) return null
        return this.readyTasks.find((task) => task.taskId !== taskId) ?? null
    }

    private restoreDailyState(state: PersistedQueueModeDay): void {
        this.activeElapsedMs = state.activeElapsedMs
        this.activeSliceStartedAtMs = null
        this.closedTaskCount = state.closedTaskCount
        this.handledTaskCount = state.handledTaskCount
        this.handledTaskIds = new Set(state.handledTaskIds)
        this.launchedTaskCount = state.launchedTaskCount
        this.snoozedTaskCount = state.snoozedTaskCount
        this.workingDayKey = state.dayKey
    }

    private resetDailyState(nowMs: number): void {
        this.activeElapsedMs = 0
        this.activeSliceStartedAtMs = null
        this.closedTaskCount = 0
        this.handledTaskCount = 0
        this.handledTaskIds.clear()
        this.launchedTaskCount = 0
        this.snoozedTaskCount = 0
        this.workingDayKey = localDayKey(nowMs)
    }

    private ensureWorkingDay(nowMs: number): void {
        const nextDayKey = localDayKey(nowMs)
        if (nextDayKey === this.workingDayKey) return

        const timerWasRunning = this.activeSliceStartedAtMs !== null
        const carriedElapsedMs = this.activeSliceStartedAtMs === null ? 0 : Math.max(0, nowMs - Math.max(this.activeSliceStartedAtMs, localDayStartMs(nowMs)))
        this.resetDailyState(nowMs)
        this.activeElapsedMs = Math.min(MAX_DAILY_ELAPSED_MS, carriedElapsedMs)
        this.activeSliceStartedAtMs = timerWasRunning ? nowMs : null
        this.scheduleDayRollover()
        this.persistState()
    }

    private commitActiveSlice(nowMs: number): void {
        const idleAtMs = this.lastActivityAtMs + QUEUE_MODE_IDLE_TIMEOUT_MS
        const activeThroughMs = Math.min(nowMs, idleAtMs)
        this.ensureWorkingDay(activeThroughMs)
        if (this.activeSliceStartedAtMs === null) return
        this.activeElapsedMs = Math.min(MAX_DAILY_ELAPSED_MS, this.activeElapsedMs + Math.max(0, activeThroughMs - this.activeSliceStartedAtMs))
        if (nowMs >= idleAtMs) {
            this.activeSliceStartedAtMs = null
            this.isIdle = true
            this.clearHeartbeatTimer()
            this.ensureWorkingDay(nowMs)
            return
        }
        this.activeSliceStartedAtMs = nowMs
    }

    private resumeClock(nowMs: number): void {
        if (!this.isActive || !this.isAppFocused || this.isIdle || this.activeSliceStartedAtMs !== null) return
        this.activeSliceStartedAtMs = nowMs
        this.scheduleHeartbeat()
    }

    private pauseClock(nowMs: number): void {
        this.commitActiveSlice(nowMs)
        this.activeSliceStartedAtMs = null
        this.clearHeartbeatTimer()
        this.persistState()
    }

    @action.bound private heartbeat(): void {
        this.heartbeatTimer = null
        if (this.activeSliceStartedAtMs === null) return
        this.commitActiveSlice(Date.now())
        this.persistState()
        this.scheduleHeartbeat()
    }

    private scheduleHeartbeat(): void {
        if (typeof window === "undefined" || this.activeSliceStartedAtMs === null) return
        this.clearHeartbeatTimer()
        const nowMs = Date.now()
        const delayMs = Math.max(1, Math.min(QUEUE_MODE_HEARTBEAT_MS, nextLocalDayStartMs(nowMs) - nowMs))
        this.heartbeatTimer = window.setTimeout(this.heartbeat, delayMs)
    }

    @action.bound private rollWorkingDay(): void {
        this.dayRolloverTimer = null
        if (!this.isActive) return
        this.ensureWorkingDay(Date.now())
        this.scheduleDayRollover()
    }

    private scheduleDayRollover(): void {
        if (typeof window === "undefined" || !this.isActive) return
        this.clearDayRolloverTimer()
        const nowMs = Date.now()
        this.dayRolloverTimer = window.setTimeout(this.rollWorkingDay, Math.max(1, nextLocalDayStartMs(nowMs) - nowMs))
    }

    @action.bound private becomeIdle(): void {
        this.idleTimer = null
        if (!this.isActive || !this.isAppFocused) return
        const idleAtMs = this.lastActivityAtMs + QUEUE_MODE_IDLE_TIMEOUT_MS
        if (Date.now() < idleAtMs) {
            this.scheduleIdleTimer()
            return
        }
        this.isIdle = true
        this.pauseClock(idleAtMs)
    }

    private scheduleIdleTimer(): void {
        if (typeof window === "undefined" || !this.isActive || !this.isAppFocused || this.isIdle) return
        this.clearIdleTimer()
        this.idleTimer = window.setTimeout(this.becomeIdle, Math.max(0, this.lastActivityAtMs + QUEUE_MODE_IDLE_TIMEOUT_MS - Date.now()))
    }

    private clearHeartbeatTimer(): void {
        if (this.heartbeatTimer !== null && typeof window !== "undefined") window.clearTimeout(this.heartbeatTimer)
        this.heartbeatTimer = null
    }

    private clearDayRolloverTimer(): void {
        if (this.dayRolloverTimer !== null && typeof window !== "undefined") window.clearTimeout(this.dayRolloverTimer)
        this.dayRolloverTimer = null
    }

    private clearIdleTimer(): void {
        if (this.idleTimer !== null && typeof window !== "undefined") window.clearTimeout(this.idleTimer)
        this.idleTimer = null
    }

    private clearClockTimers(): void {
        this.clearDayRolloverTimer()
        this.clearHeartbeatTimer()
        this.clearIdleTimer()
    }

    private persistState(): void {
        if (!this.storageScope || typeof window === "undefined") return
        const state: PersistedQueueModeState = {
            maxConcurrent: this.maxConcurrent,
            soundsEnabled: this.soundsEnabled,
            daily: {
                activeElapsedMs: this.activeElapsedMs,
                closedTaskCount: this.closedTaskCount,
                dayKey: this.workingDayKey,
                handledTaskCount: this.handledTaskCount,
                handledTaskIds: [...this.handledTaskIds],
                isActive: this.isActive,
                launchedTaskCount: this.launchedTaskCount,
                snoozedTaskCount: this.snoozedTaskCount,
                trackedTaskId: this.trackedTaskId,
            },
        }
        try {
            window.localStorage.setItem(queueModeSettingsKey(this.storageScope), JSON.stringify(state))
        } catch {
            // Q mode stores only non-secret device-local UI state and remains usable if persistence is unavailable.
        }
    }
}

export const queueModeManager = new QueueModeManager()
